import { secureRandom } from '@/lib/secureRandom';
import { useState, useEffect } from 'react';
import { useProcurementStore } from '../../../store/procurementStore';
import { Card, CardHeader, CardContent } from '../../../components/Card';
import { Button } from '../../../components/Button';
import { Badge } from '../../../components/Badge';
import { Plus, Search, ShoppingCart, Truck, CheckCircle } from 'lucide-react';
import { Input } from '../../../components/Input';
import toast from 'react-hot-toast';

export function Procurement() {
    const { orders, isLoading, fetchOrders, createOrder } = useProcurementStore();
    const [search, setSearch] = useState('');

    useEffect(() => {
        fetchOrders();
    }, []);

    const filteredOrders = orders.filter(o => 
        o.poNumber.toLowerCase().includes(search.toLowerCase()) || 
        o.vendorName.toLowerCase().includes(search.toLowerCase())
    );

    const formatCurrency = (val: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val);

    const handleCreateMock = async () => {
        try {
            await createOrder({
                vendorName: 'Acme Corp',
                totalAmount: Math.floor(secureRandom() * 5000) + 500,
                status: 'draft',
            });
            toast.success('Purchase order created');
        } catch (e) {
            toast.error('Failed to create');
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="tracking-tight text-foreground font-display text-page-title">Procurement</h1>
                    <p className="text-muted-foreground">Manage vendors, purchase orders, and goods receipts.</p>
                </div>
                <Button variant="primary" onClick={handleCreateMock}>
                    <Plus className="w-4 h-4 mr-2" />
                    Create PO
                </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card>
                    <CardContent className="p-4 flex items-center gap-4">
                        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
                            <ShoppingCart className="w-5 h-5" />
                        </div>
                        <div>
                            <p className="text-sm font-medium text-muted-foreground">Draft POs</p>
                            <h3 className="text-foreground font-display text-metric">
                                {orders.filter(o => o.status === 'draft').length}
                            </h3>
                        </div>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="p-4 flex items-center gap-4">
                        <div className="w-10 h-10 rounded-lg bg-warning/10 flex items-center justify-center text-warning">
                            <Truck className="w-5 h-5" />
                        </div>
                        <div>
                            <p className="text-sm font-medium text-muted-foreground">In Transit</p>
                            <h3 className="text-foreground font-display text-metric">
                                {orders.filter(o => o.status === 'in_transit').length}
                            </h3>
                        </div>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="p-4 flex items-center gap-4">
                        <div className="w-10 h-10 rounded-lg bg-success/10 flex items-center justify-center text-success">
                            <CheckCircle className="w-5 h-5" />
                        </div>
                        <div>
                            <p className="text-sm font-medium text-muted-foreground">Completed</p>
                            <h3 className="text-foreground font-display text-metric">
                                {orders.filter(o => o.status === 'completed').length}
                            </h3>
                        </div>
                    </CardContent>
                </Card>
            </div>

            <Card>
                <CardHeader>
                    <div className="flex justify-between items-center">
                        <h3 className="text-foreground font-display text-section-title">Purchase Orders</h3>
                        <div className="relative w-64">
                            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                            <Input 
                                placeholder="Search POs..." 
                                className="pl-9"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                        </div>
                    </div>
                </CardHeader>
                <CardContent className="p-0">
                    {isLoading ? (
                        <div className="p-8 text-center text-muted-foreground">Loading...</div>
                    ) : (
                        <table className="w-full text-sm text-left">
                            <thead className="bg-secondary/50 text-muted-foreground font-semibold border-y border-border">
                                <tr>
                                    <th className="px-6 py-3">PO Number</th>
                                    <th className="px-6 py-3">Vendor</th>
                                    <th className="px-6 py-3">Amount</th>
                                    <th className="px-6 py-3">Status</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-border">
                                {filteredOrders.length === 0 ? (
                                    <tr>
                                        <td colSpan={4} className="px-6 py-8 text-center text-muted-foreground">
                                            No purchase orders found.
                                        </td>
                                    </tr>
                                ) : filteredOrders.map(op => (
                                    <tr key={op.id} className="hover:bg-secondary/20 transition-colors">
                                        <td className="px-6 py-4 font-medium text-foreground">{op.poNumber}</td>
                                        <td className="px-6 py-4">{op.vendorName}</td>
                                        <td className="px-6 py-4">{formatCurrency(op.totalAmount)}</td>
                                        <td className="px-6 py-4">
                                            <Badge variant={op.status === 'completed' ? 'success' : op.status === 'draft' ? 'secondary' : 'primary'}>
                                                {op.status.toUpperCase()}
                                            </Badge>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
